import { type FormEvent, useEffect, useRef, useState } from 'react';

/**
 * 全屏登录视图：POST /api/auth/login 成功后回调 onSuccess。
 * 会话凭 HttpOnly Cookie 维持，前端不经手令牌。
 */
const LoginView = ({ onSuccess }: { onSuccess: () => void }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 登录屏唯一交互就是密码框，挂载后聚焦（用 ref 而非 autoFocus，
  // 避免无障碍告警）。
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (response.status === 204) {
        onSuccess();
        return;
      }
      if (response.status === 401) setError('密码不正确');
      else if (response.status === 429)
        setError('尝试次数过多，请一分钟后再试');
      else if (response.status === 404) setError('服务端未启用访问密码');
      else setError(`登录失败（${response.status}）`);
    } catch {
      setError('无法连接服务');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full items-center justify-center bg-neutral-50">
      <form
        onSubmit={submit}
        className="w-full max-w-xs rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
      >
        <div className="flex items-baseline gap-1.5">
          <span className="text-lg font-semibold text-neutral-900">nota</span>
          <span className="text-xs text-neutral-400">os</span>
        </div>
        <p className="mt-1 text-xs text-neutral-500">输入访问密码继续</p>
        <input
          ref={inputRef}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-4 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-900 outline-none focus:border-neutral-500"
          placeholder="访问密码"
        />
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={!password || busy}
          className="mt-4 w-full rounded-md bg-neutral-900 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-700 disabled:opacity-50"
        >
          {busy ? '登录中…' : '登录'}
        </button>
      </form>
    </div>
  );
};

export default LoginView;
